/* eslint-disable indent */
import styled from 'styled-components';
import { rgba, darken } from 'polished';
import { Button, Grid } from '@material-ui/core';

// CONSTANTS
import { BLACK, WHITE, GRAY_SUIT, RADICAL_RED } from '@/constants/colors';

// UTILITY
import { breakpoints } from '@/utility/breakpoints';

export const HeroContainer = styled.div`
  position: relative;
  width: 100%;

  background-image: ${p => `url(${p.src})` || 'none'};
  background-position: center center;
  background-size: cover;
  background-repeat: no-repeat;

  object-fit: cover;

  .MuiSkeleton-root {
    transform: none;
    background-color: ${rgba(WHITE, 0.5)};
  }

  .skeleton {
    &__logo {
      max-width: 175px;
      height: 132px;
      width: 100%;
      border-radius: 20px;
      display: inline-block;
      margin-bottom: 23px;
    }

    &__title {
      height: 60px;
      width: 50%;
      margin-bottom: 24px;
    }

    &__desc {
      margin-bottom: 10px;

      > .MuiSkeleton-root {
        margin-bottom: 8px;
        height: 25px;
      }
    }

    &__anch {
      height: 27px;
      width: 137px;

      + i {
        margin: 0 30px;
      }
    }

    &__btn {
      min-width: 132px;
      height: 50px;
      border-radius: 25px;

      &--have {
        width: 195px;
      }
    }
  }

  ${breakpoints.down('xs')} {
    background-image: url('https://media.mybundle.tv/cp-uploads/bg-mobile-2-jpg.jpg');

    .skeleton {
      &__logo {
        max-width: 120px;
        height: 90px;
      }

      &__title {
        height: 30px;
      }

      &__desc {
        > .MuiSkeleton-root {
          margin-bottom: 12px;
        }
      }

      &__anch {
        width: calc(50% - 15px);
        margin-right: 15px;
        margin-bottom: 24px;

        + i {
          display: none;
        }
      }

      &__btn {
        width: 100%;
      }
    }
  }
`;

export const Darkness = styled.div`
  background-color: ${rgba(BLACK, 0.8)};
`;

export const Container = styled.div`
  padding: 140px 0 195px;
  position: relative;
  z-index: 2;

  ${breakpoints.down('md')} {
    padding: 96px 0 124px;
  }

  ${breakpoints.down('xs')} {
    padding: 52px 0;
  }
`;

export const MainInfo = styled.div`
  margin-bottom: 40px;

  img {
    margin-bottom: 16px;
  }

  h1 {
    margin-bottom: 24px;
    font-weight: 600;
  }

  ${breakpoints.down('xs')} {
    h1 {
      font-size: 3rem;
      line-height: 3rem;
    }

    p {
      font-size: 1.8rem;
      line-height: 2.4rem;
    }
  }
`;

export const AppLogo = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  max-width: 175px;
  width: 100%;
  min-height: 130px;
  background: ${WHITE};
  border-radius: 20px;
  padding: 10px;
  margin-bottom: 16px;

  img {
    display: block;
    width: 100%;
    margin-bottom: 0;
  }

  ${breakpoints.down('xs')} {
    border-radius: 15px;
    max-width: 120px;
    min-height: 90px;
  }
`;

export const AnchorsContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 40px;
  flex-wrap: wrap;

  & > div {
    margin-bottom: 20px;

    & > a,
    span {
      font-weight: 500;
      font-size: 2.2rem;
      line-height: 2.7rem;
    }

    & > a {
      color: ${WHITE};

      &:not(.disable-underline):hover {
        text-decoration: underline;
      }
    }

    & > i {
      margin: 0 20px;
      font-size: 18px;
    }

    &:last-of-type {
      & > i {
        display: none;
      }
    }
  }

  .MuiRating-root {
    color: ${WHITE};

    .MuiRating-iconEmpty {
      color: ${rgba(WHITE, 0.1)};
    }
  }

  ${breakpoints.down('xs')} {
    flex: 1;
    margin-bottom: 15px;
    font-size: 1.8rem;
    line-height: 2.4rem;

    & > div {
      display: flex;
      align-items: center;

      max-width: 50%;

      margin-bottom: 24px;

      &:nth-of-type(2n-1) {
        width: 50%;
        max-width: 200px;
      }

      &:nth-of-type(2n) {
        width: 100%;
      }

      & > i {
        display: none;
      }
    }
  }
`;

export const PriceContainer = styled.div`
  &.price-container,
  .price-container {
    display: flex;
    align-items: center;

    &__deal-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      transform: translate(0, 25%);

      .price-container__cost {
        margin-bottom: 0px;
        position: relative;
        &:before {
          content: '';
          position: absolute;
          right: -3px;
          left: -3px;
          top: 50%;
          transform: translate(0, -50%);
          height: 1px;
          background-color: ${RADICAL_RED};
        }
      }
    }

    &__deal-cost {
      font-weight: bold;
    }
  }

  ${breakpoints.down('xs')} {
    .price-container__deal-cost {
      margin-bottom: 14px;
    }
  }
`;

export const ButtonsContainer = styled(Grid)`
  ${breakpoints.down('xs')} {
    .MuiButton-sizeLarge.MuiButton-outlinedSizeLarge {
      padding-right: 10px;
      padding-left: 10px;
    }
  }

  .buttons-apps-list {
    position: relative;
    height: 100%;

    .MuiButtonGroup-root {
      height: 100%;
    }

    .loader {
      background-color: ${rgba(BLACK, 0.7)};
    }

    ${breakpoints.down('xs')} {
      display: flex;

      .MuiButton-root {
        min-width: auto !important;
      }
    }
  }

  &.buttons__phone-number {
    max-width: 520px;

    .phone-number__button svg {
      font-size: 2.4rem;
      margin: -4px;
    }

    .buttons-apps-list {
      .MuiButton-root {
        padding: 1.5rem 1rem;
        color: ${WHITE};
        border-color: ${WHITE};

        & + .MuiButton-root {
          margin-left: 4px;
        }
      }
    }
  }
`;

export const ButtonStyled = styled(Button).attrs(({ theme: { partnerStyles } }) => ({
  color: partnerStyles ? 'secondary' : 'default',
  variant: partnerStyles ? 'contained' : 'outlined',
}))`
  border-color: ${p => (p.theme.partnerStyles ? p.theme.palette.secondary.main : WHITE)};
  color: ${p => (p.theme.partnerStyles ? p.theme.palette.secondary.contrastText : WHITE)};
  transition: color 0.25s ease;
  min-width: 200px;

  &:hover {
    color: ${p => {
      return p.theme.partnerStyles ? p.theme.palette.secondary.contrastText : darken(0.8, WHITE);
    }};
  }

  ${breakpoints.down('xs')} {
    min-width: 0;
  }
`;

export const DisclaimerWrapper = styled.div`
  position: relative;
  width: 100%;
  .disclamer-text {
    position: absolute;
    top: 16px;
    font-size: 15px;
    color: ${GRAY_SUIT};
    width: 100%;
    text-align: center;
    padding: 0 12px;
    line-height: 1.1;
    ${breakpoints.down('xs')} {
      text-align: left;
      position: relative;
      top: 24px;
      font-size: 13px;
    }
  }
`;
export const SocialWrapper = styled.div`
  position: absolute;
  top: 50%;
  right: 0;
  left: 0;
  transform: translate(0, -50%);

  ${breakpoints.down('md')} {
    left: auto;
    z-index: 3;
  }
`;

export const SocialContainer = styled.div`
  max-width: 1320px;
  margin: 0 auto;
  padding-right: 15px;
  font-size: 2.4rem;

  .icon-twitter {
    font-size: 1.9rem;
  }

  .icon-link {
    font-size: 2rem;
  }

  ul.share {
    width: 25px;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-left: auto;
  }

  li.share__item {
    a {
      color: inherit;
    }

    &:not(:last-of-type) {
      margin-right: 0;
      margin-bottom: 35px;
    }
  }
`;

export default HeroContainer;
